import { Menu as MenuIcon, Search } from 'lucide-react'
import { NotificationsBell } from './notifications'
import { OrgSwitcher } from './org-switcher'
import { ICON_BTN_STYLE } from './popup-style'
import { ThemeToggle } from './theme-toggle'
import { UserMenu } from './user-menu'

/**
 * 56px topbar: hamburger (<1024px), org switcher, global search trigger
 * (⌘K), notifications, theme toggle, user menu.
 */

/**
 * Prefer the modern `navigator.userAgentData.platform` (navigator.platform is
 * deprecated and empty on some engines, which would mis-show "Ctrl K" to Mac
 * users); fall back to a userAgent regex.
 */
function detectMac(): boolean {
  if (typeof navigator === 'undefined') return false
  const uaPlatform = (
    navigator as Navigator & { userAgentData?: { platform?: string } }
  ).userAgentData?.platform
  if (uaPlatform) return /mac/i.test(uaPlatform)
  return /Mac|iPhone|iPad/i.test(navigator.userAgent)
}

const IS_MAC = detectMac()

export interface TopbarProps {
  showHamburger: boolean
  onToggleMobileNav: () => void
  onOpenPalette: () => void
}

function SearchButton({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      className="it-icon-btn it-shell-btn"
      onClick={onOpen}
      aria-label="Open command palette"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        height: 34,
        width: '100%',
        maxWidth: 520,
        padding: '0 12px',
        borderRadius: 'var(--r-sm)',
        border: '1px solid var(--border)',
        background: 'var(--bg)',
        color: 'var(--text-mute)',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: 13,
      }}
    >
      <Search size={14} aria-hidden />
      <span>Search…</span>
      <kbd
        aria-hidden
        style={{
          marginLeft: 'auto',
          fontFamily: 'var(--ff-mono)',
          fontSize: 10.5,
          color: 'var(--text-faint)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-xs)',
          padding: '1px 5px',
          background: 'var(--bg-elev)',
        }}
      >
        {IS_MAC ? '⌘K' : 'Ctrl K'}
      </kbd>
    </button>
  )
}

export function Topbar({ showHamburger, onToggleMobileNav, onOpenPalette }: TopbarProps) {
  return (
    <header
      style={{
        gridArea: 'topbar',
        height: 56,
        background: 'var(--bg-elev)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 16px',
        minWidth: 0,
      }}
    >
      {showHamburger && (
        <button
          type="button"
          className="it-icon-btn it-shell-btn"
          onClick={onToggleMobileNav}
          aria-label="Open navigation"
          style={ICON_BTN_STYLE}
        >
          <MenuIcon size={18} aria-hidden />
        </button>
      )}
      {/* Symmetric flex zones keep the search centered; the org switcher is
          left-aligned within the left zone, next to the sidebar edge. */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minWidth: 0,
          paddingInline: 12,
        }}
      >
        <OrgSwitcher />
      </div>
      <div style={{ flex: '0 1 520px', display: 'flex', minWidth: 0 }}>
        <SearchButton onOpen={onOpenPalette} />
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 8,
          minWidth: 0,
          paddingLeft: 12,
        }}
      >
        <NotificationsBell />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  )
}
